/*
 * Copyright (C) 2017 Spanning Cloud Apps LLC.  All rights reserved.
 */
import './backupCountHistoryModal.tpl.html';

/**
 * Module for the modal showing graphs of the number of objects backed up over time.
 */
angular
  .module('shared.backupCountHistoryModal', ['shared.daterangepicker'])
  /**
   * Controller for the backupCountHistoryModal.tpl.html template.
   */
  .controller(
    'BackupCountHistoryModalCtrl',
    function (
      $rootScope,
      $scope,
      $uibModalInstance,
      $filter,
      $timeout,
      $q,
      curTypeName,
      batchUtils,
      BasicBatchPickerStore,
      ALERT_EVENTS,
      BATCH_LIMITS
    ) {
      var METADATA = 'Metadata',
        curFirstMoment,
        curLastMoment;

      // When setting the date range we want to know whether to anchor it at the first or last date when making any
      // adjustments.
      // Published in scope for testing
      $scope.DATE_ANCHOR = {
        FIRST: 'first',
        LAST: 'last'
      };

      // The chart gets pretty crowded above this
      // Published in scope for testing
      $scope.MAX_DATE_RANGE_DAYS = 30;

      /**
       * Displays the counts for the specified object type
       *
       * @param {String} type - The object type
       */
      $scope.setDisplayedType = function (type) {
        $scope.curTypeName = type;
      };

      /**
       * Helper function to format the data the way we need it.
       *
       * (This function is tested when loadNBackups and loadBackupsByDate are tested.)
       *
       * @param {Array} data - The data set to be parsed.
       *
       * @return {Array} The formatted data object.
       */
      function formatData(data) {
        _.each(data, function (item) {
          // Date is when the backup started. Store in UTC.
          item.date = moment(item.date).utc().toDate();

          return item;
        });

        // sort in increasing date order
        data = _.sortBy(data, 'date');

        return data;
      }

      /**
       * Loads the last N backups.
       *
       * (This function is published in the scope so we can call it when testing.)
       *
       * @param {Number} count - The number of batches to load, starting with the most recent.
       *
       * @return {Promise} A promise that is resolved when $scope.batchData is set.
       */
      $scope.loadNBackups = function (count) {
        // getBatches returns only backups.
        return batchUtils.getBatches(count, /*refresh=*/ true).then(function (batchesInfo) {
          $scope.batchData = formatData(batchesInfo.rows);
        });
      };

      /**
       * Loads all batches started within the specified date range. The dates are in local time.
       *
       * (This function is published in the scope so we can call it when testing.)
       *
       * @param {Date} first - The first date of the range
       * @param {Date} last - The last date of range
       * @param {String} anchor - Whether the date range should be anchored at the 'first' or 'last' when enforcing the
       *   maximum range. Defaults to 'last'.
       *
       * @return {Promise} A promise that is resolved when $scope.batchData is set.
       */
      $scope.loadBackupsByDate = function (first, last, anchor) {
        var firstMoment, lastMoment;

        // Validate the date range and update if any adjustments are made.
        $scope.setDateRange(first, last, anchor);
        first = $scope.firstDateStore.date;
        last = $scope.lastDateStore.date;

        // Batch dates are stored in UTC so we need to query in UTC
        firstMoment = moment(first).utc();
        lastMoment = moment(last).utc();

        // Avoid a call if the dates have not changed.
        if (curFirstMoment && curLastMoment && firstMoment.isSame(curFirstMoment) && lastMoment.isSame(curLastMoment)) {
          return $q.when();
        }

        return batchUtils.getBackupsByDateRange(firstMoment.toDate(), lastMoment.toDate()).then(function (batchesInfo) {
          curFirstMoment = firstMoment;
          curLastMoment = lastMoment;

          $scope.batchData = formatData(batchesInfo.rows);
        });
      };

      /**
       * Sets the first and last dates of the displayed range. These dates are in local time.
       *
       * (This function is published in the scope so we can call it when testing.)
       *
       * @param {Date} first - The date to be shown in the 'From' date picker
       * @param {Date} last - The date to be shown in the 'To' date picker
       * @param {String} anchor - Whether the range should be anchored at the 'first' or 'last' when enforcing the maximum
       *   range. Defaults to 'last'.
       */
      $scope.setDateRange = function (first, last, anchor) {
        var now = moment(),
          endOfToday = now.clone().endOf('day'),
          firstMoment = moment(first).startOf('day'),
          lastMoment = moment(last).endOf('day'),
          daysDiff;

        // If the last date is after today, move the last date
        // TODO We wouldn't have to do this if our version of angular-bootstrap datepicker supported min-date and max-date
        if (lastMoment.isAfter(endOfToday)) {
          lastMoment = endOfToday;
        }

        // If the first date is after the last date, move the first date
        // TODO We wouldn't have to do this if our version of angular-bootstrap datepicker supported min-date and max-date
        if (firstMoment.isAfter(lastMoment)) {
          firstMoment = lastMoment.clone().startOf('day');
        }

        // Determine whether the range exceeds the maximum
        daysDiff = lastMoment.diff(firstMoment, 'days');
        if (daysDiff > $scope.MAX_DATE_RANGE_DAYS) {
          if (anchor === $scope.DATE_ANCHOR.FIRST) {
            // This will always move the last moment closer to the first moment, so we don't have to worry about the last
            // moment being in the future.
            lastMoment = firstMoment.clone().add($scope.MAX_DATE_RANGE_DAYS, 'days').endOf('day');
          } else {
            firstMoment = lastMoment.clone().subtract($scope.MAX_DATE_RANGE_DAYS, 'days').startOf('day');
          }
        }

        last = lastMoment.toDate();
        first = firstMoment.toDate();

        // Update the picker store representing the 'from' date
        $scope.firstDateStore.date = first;
        $scope.firstDateStore.selectDate = first;
        // Don't select any date in the min picker after the last date
        $scope.firstDateStore.maxDate = last;

        // Update the picker store representing the 'to' date
        $scope.lastDateStore.date = last;
        $scope.lastDateStore.selectDate = last;
        // Don't select any date in the max picker before the first date
        $scope.lastDateStore.minDate = first;
        // Don't select any date in the max picker after the current date
        $scope.lastDateStore.maxDate = endOfToday.toDate();
      };

      /**
       * Sets up the controller.
       *
       * (This function is published in the scope so we can call it when testing.)
       *
       * @return {Promise} A promise that is resolved when initialization completes.
       */
      $scope.init = function () {
        var nowMoment, nowDate, weekAgoMoment, weekAgoDate;

        /**
         * Builds the list of object types
         */
        function loadObjectTypes() {
          // Stores the counts keyed by type name
          $scope.typeCounts = {};

          // TODO: Is this the right source for the list of record types? What if the current batch is metadata-only?
          _.each(batchUtils.currentBatch.typeCounts, function (counts, type) {
            // We do not want to show metadata here. It's on the metadata tab.
            if (!counts.md && type !== METADATA) {
              $scope.typeCounts[type] = counts;
            }
          });

          // An alphabetical list of type names.
          $scope.typeList = _.sortBy(_.keys($scope.typeCounts), _.identity);
        }

        // Create a store for each of the date pickers
        $scope.firstDateStore = {};
        $scope.lastDateStore = {};

        // Set the default date range
        nowMoment = moment();
        nowDate = nowMoment.toDate();
        weekAgoMoment = nowMoment.clone().subtract(1, 'weeks');
        weekAgoDate = weekAgoMoment.toDate();
        $scope.setDateRange(weekAgoDate, nowDate, $scope.DATE_ANCHOR.LAST);

        // Initialize the controller
        loadObjectTypes();

        // Load the last N backups. The user can change the dates later to see others.
        return $scope.loadNBackups(BATCH_LIMITS.NUM_BATCHES_IN_BACKUP_COUNTS_CHART).then(function () {
          // Update the date pickers based on the date range of the loaded batches
          var first = _.first($scope.batchData).date,
            last = _.last($scope.batchData).date;

          $scope.setDateRange(first, last, $scope.DATE_ANCHOR.LAST);

          // Draw the graphs for the current object type
          $scope.setDisplayedType(curTypeName);
        });
      };

      /**
       * Watch the values of the two basic date pickers and update the range values.
       */
      function addWatchers() {
        $scope.$watch(
          function () {
            return $scope.firstDateStore.selectDate;
          },
          function (newVal, oldVal) {
            if (newVal && !_.isEqual(newVal, oldVal)) {
              $scope.loadBackupsByDate($scope.firstDateStore.date, $scope.lastDateStore.date, $scope.DATE_ANCHOR.FIRST);
            }
          }
        );

        $scope.$watch(
          function () {
            return $scope.lastDateStore.selectDate;
          },
          function (newVal, oldVal) {
            if (newVal && !_.isEqual(newVal, oldVal)) {
              $scope.loadBackupsByDate($scope.firstDateStore.date, $scope.lastDateStore.date, $scope.DATE_ANCHOR.LAST);
            }
          }
        );
      }

      /**
       * Function to cancel out of the modal.
       */
      $scope.cancel = function () {
        $uibModalInstance.dismiss('cancel');
      };

      // Initialize the controller
      $scope.showLoadingMask = true;
      $scope
        .init()
        .then(addWatchers)
        .finally(function () {
          $scope.showLoadingMask = false;
        });
    }
  )
  .directive('backupCountHistoryChart', function ($compile, $filter) {
    return {
      restrict: 'E',
      scope: {
        backupCountData: '=',
        curTypeName: '='
      },
      link: function (scope, element) {
        var MAX_X_TICK_COUNT = 10,
          MAX_Y_TICK_COUNT = 5,
          DOT_RADIUS = 4,
          // Set the size of the fixed elements
          VIEW_WD = 640,
          VIEW_HT = 175,
          VIEW_MARGIN_TOP = 10,
          VIEW_MARGIN_RIGHT = 40,
          // The legend at the bottom
          LEGEND_HT = 20,
          LEGEND_MARGIN_TOP = 10,
          // The axes
          YAXIS_TITLE_WD = 20,
          XAXIS_TITLE_HT = 20,
          XAXIS_TITLE_FONT_HT = 12,
          YAXIS_WD = 40,
          XAXIS_HT = 40,
          // The chart takes up the remaining space
          CHART_WD = VIEW_WD - YAXIS_TITLE_WD - YAXIS_WD - VIEW_MARGIN_RIGHT,
          CHART_HT = VIEW_HT - VIEW_MARGIN_TOP - XAXIS_HT - XAXIS_TITLE_HT - LEGEND_HT,
          // The types of operations we display in the history.
          OP_TYPE = Object.freeze({
            UPDATED: 'updated',
            NEW: 'new',
            DELETED: 'deleted',
            UNDELETED: 'undeleted'
          }),
          i18n = $filter('i18n'),
          pluralize = $filter('pluralize'),
          visibility = {},
          // The order in which the series lines are drawn on the chart. The last one drawn will appear on top.
          // Toggline a series off and on in the legend moves it to the top.
          drawOrder = [OP_TYPE.UNDELETED, OP_TYPE.DELETED, OP_TYPE.NEW, OP_TYPE.UPDATED],
          svg,
          chartBox,
          xAxisBox,
          yAxisBox,
          xAxisTitleBox,
          yAxisTitleBox,
          legendBox,
          xScale,
          yScale,
          xAxis,
          yAxis,
          maxRecordCount,
          newSvg,
          curData;

        /**
         * Sets up the layout and creates the SVG elements for drawing the chart.
         */
        function init() {
          var chartX,
            chartY,
            yAxisTitleX,
            yAxisTitleY,
            yAxisX,
            yAxisY,
            xAxisX,
            xAxisY,
            xAxisTitleX,
            xAxisTitleY,
            legendX,
            legendY;

          /**
           * Creates an SVG container
           *
           * @param {String} name - The name of the container
           * @param {Number} x - The x coordinate of the container
           * @param {Number} y - The y coordinate of the container
           *
           * @return {Object} The new container
           */
          function createContainer(name, x, y) {
            return svg
              .append('g')
              .attr('transform', 'translate(' + x + ',' + y + ')')
              .attr('class', name);
          }

          // Remove any existing element. We'll recreate it.
          element.find('svg').remove();
          svg = d3
            .select(element[0])
            .append('svg')
            .attr('viewBox', '0 0 ' + VIEW_WD + ' ' + VIEW_HT)
            .attr('preserveAspectRatio', 'none');

          // Create containers for each part of the view so we don't have to worry about translations when we're
          // drawing inside each container.

          // Position the chart.
          chartX = YAXIS_TITLE_WD + YAXIS_WD;
          chartY = VIEW_MARGIN_TOP;
          chartBox = createContainer('chart', chartX, chartY);

          // Position the Y axis title
          yAxisTitleX = 0;
          yAxisTitleY = chartY;
          yAxisTitleBox = createContainer('axis-title y-axis', yAxisTitleX, yAxisTitleY);

          // Position the Y axis
          yAxisX = yAxisTitleX + YAXIS_TITLE_WD;
          yAxisY = chartY;
          yAxisBox = createContainer('axis y-axis', yAxisX, yAxisY);

          // Position the X axis
          xAxisX = chartX;
          xAxisY = chartY + CHART_HT;
          xAxisBox = createContainer('axis x-axis', xAxisX, xAxisY);

          // Position the X axis title
          xAxisTitleX = chartX;
          xAxisTitleY = xAxisY + XAXIS_HT;
          xAxisTitleBox = createContainer('axis-title x-axis', xAxisTitleX, xAxisTitleY);

          // Position the legend
          legendX = chartX;
          legendY = xAxisTitleY + XAXIS_TITLE_HT + LEGEND_MARGIN_TOP;
          legendBox = createContainer('legend', legendX, legendY);
        }

        /**
         * Creates the X axis
         */
        function createXAxis() {
          var tickValues, modulus;

          // scaleBand is a variant of ordinal to display equal sized bars.
          xScale = d3
            .scaleBand()
            .domain(
              curData.map(function (batch) {
                return batch.date;
              })
            )
            .range([0, CHART_WD])
            .padding(0.1);

          // Determine where we need ticks to show no more than the maximum number of ticks
          if (curData.length <= MAX_X_TICK_COUNT) {
            tickValues = xScale.domain();
          } else {
            // Use a filter on the domain values to get the tick values. The modulus is selected to give us a number
            // of ticks between 1 and MAX_X_TICK_COUNT
            modulus = Math.floor(curData.length / MAX_X_TICK_COUNT) + 1;

            tickValues = xScale.domain().filter(function (d, i) {
              return i % modulus === 0;
            });
          }

          xAxis = d3
            .axisBottom(xScale)
            .tickValues(tickValues)
            // Return blank here because we want to draw our own in drawXAxis().
            .tickFormat('');
        }

        /**
         * Creates the Y axis
         */
        function createYAxis() {
          var tickCount;

          // Don't display more ticks than there are records
          maxRecordCount = d3.max(curData, function (batch) {
            var counts = batch.typeCounts[scope.curTypeName];
            if (!counts) {
              return 0;
            }

            return d3.max([counts.updated, counts.new, counts.deleted, counts.undeleted]);
          });

          // We don't want a domain of [0, 0]
          if (maxRecordCount === 0) {
            maxRecordCount = 1;
          }

          tickCount = Math.min(MAX_Y_TICK_COUNT, maxRecordCount);

          yScale = d3
            .scaleLinear()
            // We always want to show 0 at the bottom of the y axis
            .domain([0, maxRecordCount])
            .range([CHART_HT, 0])
            .nice();

          yAxis = d3.axisLeft(yScale).ticks(tickCount).tickFormat(d3.format(',d'));
        }

        /**
         * Draws the chart background
         */
        function drawBackground() {
          chartBox.append('rect').attr('class', 'chart-background').attr('width', CHART_WD).attr('height', CHART_HT);

          // If there is no data show a message here
          if (curData.length === 0) {
            chartBox
              .append('text')
              .attr('class', 'no-data')
              .attr('text-anchor', 'middle')
              .attr('x', CHART_WD / 2)
              .attr('y', CHART_HT / 2)
              .text(i18n('NO_BACKUPS_AVAILABLE'));
          }
        }

        /**
         * Draws the x axis, grid lines, and tick marks
         */
        function drawXAxis() {
          var texts;

          /**
           * Creates the vertical grid lines across the chart.
           *
           * @param {Object} g - the SVG container
           */
          function makeXAxisGrid(g) {
            var xAxisGrid = d3.axisBottom(xScale).tickSize(CHART_HT).tickFormat('');

            g.call(xAxisGrid);
          }

          // Draw the tick marks. 'texts' will be the set of text elements corresponding to the ticks.
          texts = xAxisBox
            .append('g')
            .attr('class', 'axis')
            .call(xAxis)
            .selectAll('g') // Select all ticks
            .selectAll('text') // Select the text within each tick
            .attr('text-anchor', 'middle');

          // Add a tspan to each tick text showing the month and day
          texts
            .append('tspan')
            .attr('x', 0)
            .text(function (date) {
              // TODO Use locale-specific format? Moment doesn't have one that doesn't include the year.
              return date ? moment(date).format('MMM D') : null;
            });

          // Add a tspan to each tick text showing the time
          texts
            .append('tspan')
            .attr('x', 0)
            .attr('dy', XAXIS_TITLE_FONT_HT)
            .text(function (date) {
              // TODO Use locale-specific time (LT) if we can find a locale-specific date, above
              return date ? moment(date).format('h:mm a') : null;
            });

          // Draw the vertical grid lines
          chartBox.append('g').attr('class', 'grid').call(makeXAxisGrid);

          // Draw the title
          xAxisTitleBox
            .append('text')
            .attr('class', 'capitalize')
            .attr('text-anchor', 'middle')
            .attr('x', CHART_WD / 2)
            .attr('y', XAXIS_TITLE_HT / 2)
            .text(i18n('BACKUP_START_TIME'));
        }

        /**
         * Draws the y axis, grid lines, and tick marks
         */
        function drawYAxis() {
          /**
           * Creates the horizontal grid lines across the chart.
           *
           * @param {Object} g - the SVG container
           */
          function makeYAxisGrid(g) {
            var tickCount, yAxisGrid;

            // Don't display more grid lines than there are records
            tickCount = Math.min(MAX_Y_TICK_COUNT, maxRecordCount);

            yAxisGrid = d3.axisLeft(yScale).ticks(tickCount).tickSize(-CHART_WD).tickFormat('');

            g.call(yAxisGrid);
          }

          // Draw the y axis and tick marks.
          yAxisBox
            .append('g')
            .attr('class', 'axis')
            // A left axis is drawn to the left of its position so we move it all the way to the right
            .attr('transform', 'translate(' + YAXIS_WD + ',0)')
            .call(yAxis);

          // Draw the horizontal grid lines
          chartBox.append('g').attr('class', 'grid').call(makeYAxisGrid);

          // Draw the rotated title
          yAxisTitleBox
            .append('text')
            .attr('class', 'capitalize')
            .attr('text-anchor', 'middle')
            .attr('transform', 'translate(' + YAXIS_TITLE_WD / 2 + ',' + CHART_HT / 2 + ') rotate(-90)')
            .text(i18n('RECORD_COUNT_TITLE'));
        }

        /**
         * Draws a line graph showing the duration for each backup
         *
         * @param {String} opType - The type of operation being counted
         */
        function drawCounts(opType) {
          var X_OFFSET = xScale.bandwidth() / 2,
            path,
            counts;

          /**
           * Determines the range location on the X axis for a specific batch.
           *
           * @param {Object} batch - The batch of interest.
           *
           * @returns {Number} The X axis range value corresponding to the batch date.
           */
          function getXValueFromBatch(batch) {
            return xScale(batch.date) + X_OFFSET;
          }

          /**
           * Returns the record count for the current record type and specified operation in the specified batch.
           *
           * @param {Object} batch - The batch to be checked.
           * @param {String} opType - The type of operation to be counted.
           *
           * @returns {Number} - The number of records in the batch of the specified record type and operation type.
           */
          function getRecordCount(batch, opType) {
            var typeCounts, count;

            typeCounts = batch.typeCounts[scope.curTypeName];
            count = typeCounts ? typeCounts[opType] : 0;

            return count;
          }

          /**
           * Determines the range location on the Y axis for a specific batch.
           *
           * @param {Object} batch - The batch of interest.
           *
           * @returns {Number} The Y axis range value corresponding to the batch record count for the current record
           *   type and operation.
           */
          function getYValueFromBatch(batch) {
            var count = getRecordCount(batch, opType);
            return yScale(count);
          }

          if (!opType) {
            // Draw in the current draw order
            _.each(drawOrder, drawCounts);
            return;
          }

          // If this operation type series is not visible skip it.
          if (!visibility[opType]) {
            return;
          }

          // Add the line graph
          counts = chartBox.append('g').attr('class', 'counts');

          // Create the path for the counts for this operation type
          path = d3.line().x(getXValueFromBatch).y(getYValueFromBatch);

          counts.append('path').datum(curData).attr('d', path).attr('class', opType);

          // Add the dots
          counts
            .selectAll('circle')
            .data(curData)
            .enter()
            .append('circle')
            .attr('class', opType)
            .attr('cx', getXValueFromBatch)
            .attr('cy', getYValueFromBatch)
            .attr('r', DOT_RADIUS)
            .attr('uib-tooltip-html', function (batch) {
              var count = getRecordCount(batch, opType),
                tooltip = '';

              // Show the backup type (manual, scheduled, custom).
              tooltip += '<div class="capitalize">' + i18n('BACKUP_TYPE_' + batch.type) + '</div>';

              // Show the start time. 'llll' is the full locale-specific date and time, like 'Thu, Sep 4 1986 8:30 PM'
              tooltip += '<div>' + moment(batch.date).format('llll') + '</div>';

              // Show the object count
              tooltip +=
                '<div class="capitalize">' + pluralize(count, 'ONE_RECORD', 'N_RECORDS') + ' ' + opType + '</div>';

              // UI Bootstrap 0.14.3 requires HTML to be a string inside a string.
              return "'" + tooltip + "'";
            })
            .attr('tooltip-append-to-body', true);
        }

        /**
         * Draws the legend for the chart.
         */
        function drawLegend() {
          var LABEL_LEFT_MARGIN = 5,
            CHECKBOX_WIDTH = 8,
            CHECKBOX_CORNER_RADIUS = 2,
            UPDATED_LABEL_WIDTH = 70,
            NEW_LABEL_WIDTH = 50,
            DELETED_LABEL_WIDTH = 70,
            CHECKBOX_Y = -4, // Trial and error...
            DOT_Y = 0,
            LABEL_Y = 4,
            // This seems like a bug in eslint
            //eslint-disable-next-line no-unused-vars
            x;

          /**
           * Draw the legend label for a particular operation type.
           *
           * @param {String} opType - The type of operation being labeled.
           * @param {Number} seriesX - The x range value for the series label.

           * @returns {Number} - The x range value of the label.
           */
          function drawSeriesLabel(opType, seriesX) {
            var dotX = CHECKBOX_WIDTH + LABEL_LEFT_MARGIN + DOT_RADIUS,
              labelX = dotX + DOT_RADIUS / 2 + LABEL_LEFT_MARGIN,
              // Watch for click to toggle visibility of this series
              clickAction = "toggleSeries('" + opType + "')",
              opTypeText = i18n(opType.toUpperCase()),
              dotClass = opType,
              checkboxClass,
              seriesBox;

            seriesBox = legendBox.append('g').attr('transform', 'translate(' + seriesX + ',0)');

            // This is the tooltip for the label
            seriesBox.append('title').text(i18n('BACKUP_COUNT_CLICK_TO_TOGGLE', [opTypeText]));

            // Style the checkbox depending on whether the series is shown or hidden
            checkboxClass = 'checkbox';
            if (visibility[opType]) {
              checkboxClass += ' checked';
            }

            // Draw the checkbox outline
            seriesBox
              .append('rect')
              .attr('class', checkboxClass)
              .attr('ng-click', clickAction)
              .attr('x', 0)
              .attr('y', CHECKBOX_Y)
              .attr('width', CHECKBOX_WIDTH)
              .attr('height', CHECKBOX_WIDTH)
              .attr('rx', CHECKBOX_CORNER_RADIUS)
              .attr('ry', CHECKBOX_CORNER_RADIUS);

            // Draw the check mark if this series is visible
            if (visibility[opType]) {
              seriesBox.append('path').attr('class', 'checkmark').attr('d', 'M1,-1l3,3l4,-8');
            }

            seriesBox
              .append('circle')
              .attr('class', dotClass)
              .attr('ng-click', clickAction)
              .attr('cx', dotX)
              .attr('cy', DOT_Y)
              .attr('r', DOT_RADIUS);

            seriesBox
              .append('text')
              .attr('class', 'primary-font-light capitalize')
              .attr('ng-click', clickAction)
              .attr('x', labelX)
              .attr('y', LABEL_Y)
              .text(opTypeText);

            return labelX;
          }

          // Show the updated counts
          x = 0;
          x += drawSeriesLabel(OP_TYPE.UPDATED, x);

          // Show the new counts
          x += UPDATED_LABEL_WIDTH;
          x += drawSeriesLabel(OP_TYPE.NEW, x);

          // Show the deleted counts
          x += NEW_LABEL_WIDTH;
          x += drawSeriesLabel(OP_TYPE.DELETED, x);

          // Show the undeleted counts
          x += DELETED_LABEL_WIDTH;
          x += drawSeriesLabel(OP_TYPE.UNDELETED, x);
        }

        /**
         * Draws all the elements of the chart.
         */
        function redraw(data) {
          if (!data) {
            return;
          }

          curData = data;

          init();
          createXAxis();
          createYAxis();
          drawBackground();

          // Only draw these elements if there is at least one data point.
          if (curData.length) {
            drawYAxis();
            drawXAxis();
            drawCounts();
            drawLegend();
          }

          // Since we are using a directive for the tooltips
          // we will need to recompile those elements.
          newSvg = element.find('svg').clone();
          newSvg = $compile(newSvg)(scope);
          element.find('svg').replaceWith(newSvg);
        }

        /**
         * Shows or hides a particular series when the legend label or dot is clicked.
         *
         * @param {String} opType - The type of operation being shown/hidden.
         */
        scope.toggleSeries = function (opType) {
          var newVis = !visibility[opType];

          visibility[opType] = newVis;

          // If this one is newly visible, move it to the end of the draw order so it appears on top in the chart.
          if (newVis) {
            drawOrder = _.without(drawOrder, opType);
            drawOrder.push(opType);
          }

          redraw(curData);
        };

        // Start with each operation type series visible
        _.each(OP_TYPE, function (opType) {
          visibility[opType] = true;
        });

        // Watch for changes to the batch data
        scope.$watch('backupCountData', redraw);

        // Watch for a new record type to be selected
        scope.$watch('curTypeName', function (newVal, oldVal) {
          if (newVal !== oldVal) {
            redraw(curData);
          }
        });
      }
    };
  });
